import { useState, useEffect } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { putRegion } from '../../api/regionAPI';

const EditRegionDialog = ({ open, onClose, regions, onRegionUpdated, regionToEdit }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentRegion: ''
  });
  const [originalCode, setOriginalCode] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // regionToEdit değiştiğinde formu doldur
  useEffect(() => {
    if (regionToEdit && open) {
      // Alt bölge görünümünde (subRegionId varsa) subRegionId'yi kullan, yoksa regionId'yi kullan
      const regionIdToEdit = regionToEdit.subRegionId || regionToEdit.regionId;
      const region = regions.find(r => r.idRegion === regionIdToEdit);

      if (region) {
        setOriginalCode(region.code || '');
        setFormData({
          name: region.name || '',
          description: region.description || '',
          parentRegion: region.parentRegion || ''
        });
      }
    }
  }, [regionToEdit, open, regions]);

  const getRegionLevel = (region, allRegions) => {
    if (!region) return 0;
    if (region.parentRegion === null) return 1;

    const parentRegion = allRegions.find(r => r.idRegion === region.parentRegion);
    if (!parentRegion) return 1;

    return getRegionLevel(parentRegion, allRegions) + 1;
  };

  const getEligibleParentRegions = () => {
    if (!regions || regions.length === 0 || !regionToEdit) return [];

    // Alt bölge görünümünde (subRegionId varsa) subRegionId'yi kullan, yoksa regionId'yi kullan
    const regionIdToEdit = regionToEdit.subRegionId || regionToEdit.regionId;
    const currentRegion = regions.find(r => r.idRegion === regionIdToEdit);

    if (!currentRegion) return [];

    const currentLevel = getRegionLevel(currentRegion, regions);

    // Mevcut parent region'ı bul (eğer varsa)
    const currentParentRegion = currentRegion.parentRegion
      ? regions.find(r => r.idRegion === currentRegion.parentRegion)
      : null;

    // Mevcut parent'ın level'ını hesapla
    const currentParentLevel = currentParentRegion
      ? getRegionLevel(currentParentRegion, regions)
      : null;

    // Parent seçenekleri: Şu anki parent ile aynı seviyedeki bölgeleri göster
    const eligibleRegions = regions.filter(region => {
      // Kendini parent olarak seçemez
      if (region.idRegion === currentRegion.idRegion) {
        return false;
      }

      const regionLevel = getRegionLevel(region, regions);

      // Eğer mevcut parent varsa, sadece onunla aynı level'daki bölgeleri göster
      if (currentParentLevel !== null) {
        return regionLevel === currentParentLevel;
      }

      // Eğer mevcut parent yoksa (Level 2 ise), sadece Level 1'leri göster
      if (currentLevel === 2) {
        return regionLevel === 1;
      }

      // Eğer mevcut level 3 ise ama parent bulunamadıysa, Level 2'leri göster
      if (currentLevel === 3) {
        return regionLevel === 2;
      }

      return false;
    });

    return eligibleRegions;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!formData.name) {
        setError(t('regions.dialogs.addRegion.nameRequired', 'Name alanı zorunludur'));
        return;
      }

      const adminId = localStorage.getItem('adminId');
      if (!adminId) {
        setError(t('regions.dialogs.addRegion.adminNotFound', 'Admin ID bulunamadı. Lütfen tekrar giriş yapın.'));
        return;
      }

      // Düzenlenen bölgenin ID'sini bul
      const regionId = regionToEdit.subRegionId || regionToEdit.regionId;

      const requestData = {
        idRegion: regionId, // ID değişmez
        code: originalCode, // Kod değişmez, orijinal kod kullanılır
        name: formData.name, // Sadece isim değişebilir
        description: formData.description,
        admin_idAdmin: parseInt(adminId), // localStorage'dan al
        parentRegion: formData.parentRegion || null,
        isdeleted: 0
      };

      await putRegion(requestData);

      setSnackbar({
        open: true,
        message: t('regions.messages.regionUpdated', 'Bölge başarıyla güncellendi'),
        severity: 'success'
      });

      if (onRegionUpdated) {
        onRegionUpdated();
      }

      onClose();
    } catch (err) {
      setError(err.message || t('regions.messages.errorUpdatingRegion', 'Bölge güncellenirken bir hata oluştu'));
      console.error('Error updating region:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const eligibleParentRegions = getEligibleParentRegions();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('regions.dialogs.editRegion.title', 'Bölgeyi Düzenle')}</DialogTitle>
      <DialogContent dividers sx={{ pb: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          margin="dense"
          name="code"
          label={t('regions.dialogs.addRegion.code', 'Kod')}
          type="text"
          fullWidth
          variant="outlined"
          value={originalCode}
          disabled={true}
          sx={{ mb: 2, mt: 1, '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)' } }}
        />

        <TextField
          margin="dense"
          name="name"
          label={t('regions.dialogs.addRegion.name', 'İsim')}
          type="text"
          fullWidth
          variant="outlined"
          value={formData.name}
          onChange={handleInputChange}
          disabled={loading}
          required
          sx={{ mb: 2 }}
        />

        <TextField
          margin="dense"
          name="description"
          label={t('regions.dialogs.addRegion.descriptionField', 'Açıklama')}
          type="text"
          fullWidth
          variant="outlined"
          multiline
          rows={3}
          value={formData.description}
          onChange={handleInputChange}
          disabled={loading}
          sx={{ mb: 2 }}
          InputProps={{
            sx: { alignItems: 'flex-start' }
          }}
        />

        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
          <InputLabel>{t('regions.dialogs.addRegion.parentRegion', 'Üst Bölge')}</InputLabel>
          <Select
            name="parentRegion"
            value={formData.parentRegion}
            onChange={handleInputChange}
            label={t('regions.dialogs.addRegion.parentRegion', 'Üst Bölge')}
            disabled={loading}
          >
            <MenuItem value="">
              <em>{t('regions.dialogs.addRegion.none', 'Yok (Ana Bölge)')}</em>
            </MenuItem>
            {eligibleParentRegions.map((region) => {
              const level = getRegionLevel(region, regions);
              const levelLabel = level === 1 ? 'Root' : level === 2 ? 'Region' : `Level ${level}`;
              return (
                <MenuItem key={region.idRegion} value={region.idRegion}>
                  {region.name} ({region.code}) - {levelLabel}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel', 'İptal')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? t('common.saving', 'Kaydediliyor...') : t('common.save', 'Kaydet')}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default EditRegionDialog;
